import * as dbAccessor from '@/utils/dbAccessor'
import { convertTimestampToDateInObj } from '@/utils/tools'

export default {
  state: {
    combinedUpcs: []
  },

  mutations: {
    setCombinedUpcList (state, list) {
      state.combinedUpcs = list
    }
  },

  actions: {
    getCombinedUpcRT ({ commit, rootState, getters }) {
      const { activeOrganization: organizationKey } = getters

      let successFunc = (docs) => {
        let products = []
        docs.forEach((doc) => {
          const obj = convertTimestampToDateInObj(doc.data())
          const id = doc.id
          obj.onlyForSearch = [
            ...obj.components.map((item) => {
              return item.upc
            }),
            ...(obj.sku || []),
            obj.note || ''
          ].join()
          products.push({ ...obj, id })
        })
        // commit('setProducts', products)
        commit('setCombinedUpcList', products)
      }

      const errorFunc = (error) => {
        console.error('Combined UPC RT error:', error)
      }

      const unsubscribe = dbAccessor.queryStoreRT(
        successFunc,
        errorFunc,
        [],
        'tenants',
        organizationKey,
        'combinedUpc'
      )

      commit('addTosubscribed', { combinedUpcList: unsubscribe })
    },

    async createCombinedUpc ({ getters }, data) {
      const { activeOrganization: organizationKey } = getters
      return dbAccessor.insertStore(
        {
          ...data,
          organizationKey,
          isArchived: false
        },
        'tenants',
        organizationKey,
        'combinedUpc'
      )
    },
    async updateCombinedUpc ({ getters }, data) {
      const { activeOrganization: organizationKey } = getters
      return dbAccessor.updateFieldsStore(
        {
          ...data
        },
        'tenants',
        organizationKey,
        'combinedUpc',
        data.id
      )
    },
    async getArchiveCombined ({ getters }, payload) {
      const { activeOrganization } = getters
      let predicates = []

      if (payload.startDate && payload.endDate) {
        predicates = [
          ...predicates,
          {
            field: 'createTime',
            compare: '>=',
            value: payload.startDate
          },
          {
            field: 'createTime',
            compare: '<=',
            value: payload.endDate
          }
        ]
      }

      if (payload.predicates?.predicateText) {
        predicates.push({
          field: 'searchKeywords',
          compare: 'array-contains',
          value: payload.predicates.predicateText.toLowerCase()
        })
      }

      return dbAccessor.queryWithPagination(
        predicates,
        ['tenants', activeOrganization, 'archivedCombinedUpc'],
        'createTime',
        true,
        payload?.startAfter,
        payload?.limit
      )
    },
    async archiveCombinedUpc ({ getters }, id) {
      const { activeOrganization: organizationKey } = getters

      return dbAccessor.runInTransactionStore(async (transaction) => {
        const docRef = dbAccessor.buildStoreQuery([
          'tenants',
          organizationKey,
          'combinedUpc',
          id
        ])

        const doc = await transaction.get(docRef)
        if (!doc.exists) {
          throw new Error('Combined UPC not found')
        }

        const archivedDocRef = dbAccessor.buildStoreQuery([
          'tenants',
          organizationKey,
          'archivedCombinedUpc',
          id
        ])

        const data = doc.data()
        transaction.set(archivedDocRef, dbAccessor.addNewDocTimestamp(data))

        transaction.delete(docRef)
      })
    },

    async getCombinedUpcStock ({ getters }, id) {
      const { activeOrganization: organizationKey } = getters
      const doc = await dbAccessor.queryWithPredicatesStore(
        [{ field: 'id', compare: '==', value: id }],
        'tenants',
        organizationKey,
        'combinedUpc'
      )

      if (!doc.exists) {
        throw new Error('Combined UPC not found')
      }

      const upc = doc.data()
      const stocks = await Promise.all(
        upc.components.map(async (comp) => {
          const stock = await dbAccessor.queryWithPredicatesStore(
            [{ field: 'upc', compare: '==', value: comp.upc }],
            'tenants',
            organizationKey,
            'stocks'
          )

          return {
            upc: comp.upc,
            name: comp.name,
            stock: Math.floor((stock?.quantity || 0) / comp.quantity)
          }
        })
      )

      return stocks
    }
  },
  getters: {
    getProductStock: (state, getters, rootState, rootGetters) => (upc) => {
      const products = rootGetters.products || []
      return products.filter((p) => p.upc === upc)
    },
    calculateQuantity: (state, getters) => (combinedUpc) => {
      const stockByLocation = combinedUpc.components.reduce(
        (acc, component) => {
          const productStocks = getters.getProductStock(component.upc)
          
          if (!productStocks.length) {
            return acc
          }
          productStocks.forEach((inventory) => {
            if (!inventory.distribution) return acc
            Object.keys(inventory.distribution).forEach((locationKey) => {
              const location = inventory.distribution[locationKey]
              const { 
                warehouseSite, 
                siteName, 
                quantity, 
                uid, 
                userName, 
                warehouseKey 
              } = location

              if (!acc[warehouseSite]) {
                acc[warehouseSite] = {
                  siteName,
                  quantity: Infinity,
                  warehouseSite,
                  warehouseKey,
                  uid,
                  userName,
                  components: []
                }
              }
              acc[warehouseSite].components.push({
                id: inventory.id,
                upc: inventory.upc, 
                name: inventory.name, 
                sku: inventory.sku, 
                quantity,
                requiredQty: component.quantity
              })
              const availableSets = Math.floor(quantity / component.quantity)
              acc[warehouseSite].quantity = Math.min(
                acc[warehouseSite].quantity,
                availableSets
              )
            })
          })
          return acc
        },
        {}
      )
      const values = Object.values(stockByLocation)
      return values.filter((location) => location.quantity > 0 && location.components.length === combinedUpc.components.length)
    },
  
    combinedUpcsWithLocations: (state, getters) => {
      return state.combinedUpcs.map(upc => {
        const locations = getters.calculateQuantity(upc)
        return {
          ...upc,
          isCombined: true,
          locations: locations,
          totalQuantity: locations.reduce((total, item) => total + item.quantity, 0)
        }
      })
    },
  
    combinedUpcs: (state, getters) => {
      return getters.combinedUpcsWithLocations
    },
    
    combinedUpcsByLocation: (state, getters) => {
      return getters.combinedUpcs.reduce((arr, item) => {
        const { totalQuantity, locations, ...rest } = item
        locations.forEach(location => {
          const newItem = {
            ...rest,
            ...location, 
            fbmKey: `${item.id}-${location.warehouseKey}-${location.warehouseSite}`
          }
          
          arr.push(newItem)
        })
        return arr
      }, []) || []
    }
  }
}



// WEBPACK FOOTER //
// ./src/store/combinedUpc/index.js